import { AuditOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { api } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import { AuthorityBody } from '../pages/Authority.js';
import { ExportButtons } from './ExportButtons.js';

/**
 * ใครเป็นคนลงนามแต่งตั้ง — หน้าต่างบนผัง
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องกลับเข้ามาอยู่บนผัง
 * ────────────────────────────────────────────────────────────────
 *
 * ตอนยุบเมนูเจ็ดอันทิ้ง หน้านี้ถูกยกออกไปพร้อมกับหน้าอื่น ไฟล์ยังอยู่และ API
 * ยังทำงาน แต่ไม่มีทางกดเข้าถึงได้เลย ผลคือคำถามข้อ "ใครเป็นผู้มีอำนาจสั่งบรรจุ
 * แต่งตั้งในแต่ละระดับ" ระบบตอบได้อยู่แล้วแต่ผู้ใช้หาไม่เจอ ซึ่งในทางปฏิบัติ
 * ไม่ต่างจากตอบไม่ได้
 *
 * มันคู่กับผังพอดี เพราะคำถามนี้มักตามหลังคำถามอื่นทันที — เห็นอัตราว่างระดับ
 * อำนวยการสูงในผัง คำถามถัดไปคือใครเซ็นแต่งตั้งได้ ไม่ใช่คำถามที่ต้องออกจากผัง
 * ไปหาที่หน้าอื่น
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ตารางนี้ยังไม่ครบ และนั่นคือสิ่งที่ต้องเห็น
 * ────────────────────────────────────────────────────────────────
 *
 * ช่องที่ยังไม่มีเอกสารรองรับขึ้นว่า "ยังไม่ได้กรอก" ไม่ใช่เดาให้ และตัวเลข
 * "กรอกครบแล้ว x/y" อยู่บนสุดโดยตั้งใจ ผู้มีอำนาจสั่งบรรจุกำหนดไว้ในมาตรา 57
 * แห่ง พ.ร.บ. ระเบียบข้าราชการพลเรือน พ.ศ. 2551 การกรอกผิดแม้ช่องเดียว
 * หมายถึงคำสั่งแต่งตั้งที่อาจไม่ชอบด้วยกฎหมาย
 *
 * เนื้อในทั้งหมดมาจาก pages/Authority.tsx → AuthorityBody ตัวเดียวกัน
 * ห้ามคัดลอกมาวางซ้ำที่นี่ ไม่งั้นจะมีสองสำเนาที่แก้ตามกันไม่ทัน
 */
export function AuthorityModal({ onClose }: { onClose: () => void }) {
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  /*
   * ขอข้อมูลชุดเดียวกับที่ AuthorityBody ใช้ — react-query จะคืนค่าจากแคชให้
   * ไม่ได้ยิงซ้ำ เพราะ queryKey เดียวกัน
   */
  const q = useQuery({ queryKey: ['authority'], queryFn: api.authority });

  const build = useCallback((): ExportTable => {
    const rows = q.data?.rows ?? [];
    return {
      title: 'ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง',
      subtitle: 'ตามมาตรา 57 แห่ง พ.ร.บ. ระเบียบข้าราชการพลเรือน พ.ศ. 2551',
      /*
       * ⚠ คำเตือนมาจากเซิร์ฟเวอร์ ไม่ได้พิมพ์ไว้ที่นี่
       * วันที่นิติกรตรวจตัวบทเสร็จแล้วพลิก verified เป็น true คำเตือนจะหายไปเอง
       * ทั้งบนหน้าจอและในไฟล์ที่ดาวน์โหลด โดยไม่ต้องแก้ไฟล์นี้
       */
      warnings: q.data?.warnings ?? [],
      columns: [
        { key: 'level', label: 'ระดับตำแหน่ง', width: 26 },
        { key: 'label', label: 'ครอบคลุมตำแหน่ง', width: 46 },
        { key: 'by', label: 'ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง', width: 34 },
        { key: 'approve', label: 'ผู้ให้ความเห็นชอบหรืออนุมัติ', width: 40 },
        { key: 'basis', label: 'ฐานทางกฎหมาย', width: 30 },
        { key: 'checked', label: 'สถานะการสอบทาน', width: 16 },
      ],
      rows: rows.map((r) => ({
        level: r.dpisLevel,
        label: r.label,
        by: r.appointedBy ?? 'ยังไม่ได้กรอก',
        approve: r.approvedBy ?? '—',
        basis: r.legalBasis ?? '—',
        checked: r.verified ? 'ตรวจแล้ว' : 'ยังไม่ได้ตรวจ',
      })),
    };
  }, [q.data]);

  /* จังหวะเดียวกับหน้าต่างอื่นทุกบานในระบบ — โฟกัสเข้าปุ่มปิด, Esc ปิด,
     ล็อกการเลื่อนพื้นหลัง แล้วคืนโฟกัสกลับที่เดิมตอนปิด */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="pmodal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pmodal is-wide" role="dialog" aria-modal="true" aria-label="ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><AuditOutlined /></span>
          <div>
            <span className="kind">ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง</span>
            <b>ตามมาตรา 57 แห่ง พ.ร.บ. ระเบียบข้าราชการพลเรือน พ.ศ. 2551</b>
            <span className="sub">
              ผู้มีอำนาจสั่งบรรจุ ผู้ให้ความเห็นชอบ และขั้นตอนของแต่ละระดับตำแหน่ง
            </span>
          </div>
          {q.data && <ExportButtons build={build} />}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        <div className="authbody">
          <AuthorityBody />
        </div>
      </div>
    </div>
  );
}
